import React from 'react';
import AppChart, {
  ChartSlice,
  ChartSeries,
} from 'shared/components/Chart/AppChart';
import StatTile from 'shared/components/Dashboard/StatTile';
import MetricCard from 'shared/components/Dashboard/MetricCard';
import { EntityDetail } from '../../shared/json/reportDrillData';
import './EntityDetailView.scss';

export interface EntityDetailViewProps {
  /** KPI/metrics/chart bundle for the district or block. */
  detail: EntityDetail;
  /** Year shown in the highlighted KPI tile, e.g. "2026-27". */
  year: string;
  /** Label on the back button, e.g. "Back to Tamil Nadu — Districts". */
  backLabel: string;
  /** "home" pill text on the right, e.g. "Tamil Nadu". */
  homeLabel: string;
  /** Go back up one level. */
  onBack: () => void;
}

/** Indian short format: 1,08,46,110 → "10.85 L" / "1.22 Cr". */
const formatShort = (n: number): string => {
  if (n >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `${(n / 1e5).toFixed(2)} L`;
  return n.toLocaleString('en-IN');
};

const formatIndian = (n: number): string => n.toLocaleString('en-IN');

/**
 * Overview shown when the user drills into a District (→ Blocks) or a Block
 * (→ Schools). Mirrors the top-level Numbers layout — KPI stat tiles,
 * secondary metrics and the four charts — but scoped to the selected entity,
 * with a "Back to …" bar in place of the compliance chart.
 */
const EntityDetailView: React.FC<EntityDetailViewProps> = ({
  detail,
  year,
  backLabel,
  homeLabel,
  onBack,
}) => {
  const { kpi, metrics } = detail;

  const managementSlices = detail.managementWise as ChartSlice[];
  const genderSlices = detail.genderSplit as ChartSlice[];
  const socialSlices = detail.socialCategory as ChartSlice[];
  const enrolmentSeries: ChartSeries[] = [
    { label: 'Boys', data: detail.enrolmentByGrade.boys, color: '#26a69a' },
    { label: 'Girls', data: detail.enrolmentByGrade.girls, color: '#f0803c' },
  ];

  return (
    <div className="entity-detail">
      {/* ── KPI stat tiles ── */}
      <div className="row g-3 mb-3">
        <div className="col-6 col-md-4 col-xl">
          <StatTile
            icon="pi pi-building"
            value={formatIndian(kpi.schools)}
            subValue={formatIndian(kpi.schools)}
            label="Total Schools"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <StatTile
            icon="pi pi-users"
            value={formatShort(kpi.students)}
            subValue={formatIndian(kpi.students)}
            label="Total Students"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <StatTile
            icon="pi pi-user"
            value={formatShort(kpi.boys)}
            subValue={formatIndian(kpi.boys)}
            label="Boys"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <StatTile
            icon="pi pi-user"
            value={formatShort(kpi.girls)}
            subValue={formatIndian(kpi.girls)}
            label="Girls"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <StatTile
            icon="pi pi-briefcase"
            value={formatIndian(kpi.teachers)}
            subValue={formatIndian(kpi.teachers)}
            label="Teachers"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <StatTile
            variant="solid"
            value={detail.name}
            label={`${year} · ${detail.levelLabel}`}
            note="Source: EMIS"
          />
        </div>
      </div>

      {/* ── Secondary metrics ── */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-lg-3">
          <MetricCard
            value={metrics.gpi}
            label="Gender Parity Index"
            accent="#22c55e"
          />
        </div>
        <div className="col-6 col-lg-3">
          <MetricCard value={metrics.ptr} label="Pupil-Teacher Ratio" />
        </div>
        <div className="col-6 col-lg-3">
          <MetricCard
            value={metrics.girlsShare}
            label="Girls Share"
            accent="#f0803c"
          />
        </div>
        <div className="col-6 col-lg-3">
          <MetricCard
            value={metrics.avgStudents}
            label="Avg Students / School"
          />
        </div>
      </div>

      {/* ── Four overview charts ── */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-6 col-xl-3">
          <AppChart
            type="doughnut"
            title={`Management-Wise — ${detail.name}`}
            icon="pi pi-chart-pie"
            iconColor="#2f5fb0"
            slices={managementSlices}
            height={320}
          />
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <AppChart
            type="doughnut"
            title="Gender Split"
            icon="pi pi-users"
            iconColor="#7e57c2"
            slices={genderSlices}
            height={320}
          />
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <AppChart
            type="doughnut"
            title="Social Category"
            icon="pi pi-th-large"
            iconColor="#26a69a"
            slices={socialSlices}
            height={320}
          />
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <AppChart
            type="stackedBar"
            title="Enrolment by Grade"
            icon="pi pi-chart-bar"
            iconColor="#f0803c"
            labels={detail.enrolmentByGrade.grades}
            series={enrolmentSeries}
            yTitle="Students"
            height={320}
          />
        </div>
      </div>

      {/* ── Back / context bar (replaces the compliance chart here) ── */}
      <div className="entity-detail__bar">
        <button type="button" className="entity-detail__back" onClick={onBack}>
          <i className="pi pi-arrow-left" /> {backLabel}
        </button>
        <span className="entity-detail__viewing">
          Currently viewing: <strong>{detail.name}</strong>
        </span>
        <span className="entity-detail__home">
          <i className="pi pi-home" /> {homeLabel}
        </span>
      </div>
    </div>
  );
};

export default EntityDetailView;
